import React, { CSSProperties, ReactNode, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import classNames from 'classnames';
import { PlayerState } from '../playerState';
import { PlayerPlaybar } from './playerPlaybar';
import { PlayerControlsBar } from './controlsBar';
import { PlayerComponentName } from '../playerSchema';
import { DownloadSVGIcon, PlayerBookmarkSVGIcon, PodcastPlaceholderSvg, ShareSVGIcon } from '../../svg';
import { PlayerToggleBar } from './toggleBar';
import { PlayerMarkerBar } from './markerBar';
import { ToggleButton } from './toggleButton';

type Props = {
  model: PlayerState;
  mediaSlot?: ReactNode;
  showInlineVideo?: boolean;
}

const waveformBars = [10, 14, 9, 18, 13, 23, 17, 28, 21, 32, 24, 36, 27, 31, 22, 34, 26, 19, 29, 16, 22, 13, 18, 10, 15, 24, 20, 30, 25, 18, 33, 21, 27, 14, 19, 11];
const waveformBarWidth = 3;
const waveformBarGap = 6;
const waveformHeight = 44;
const waveformWidth = waveformBars.length * waveformBarWidth + (waveformBars.length - 1) * waveformBarGap;

type AudioWindow = Window & typeof globalThis & {
  webkitAudioContext?: typeof AudioContext;
}

type CapturableVideoElement = HTMLVideoElement & {
  captureStream?: () => MediaStream;
  mozCaptureStream?: () => MediaStream;
}

const useAudioWaveformBars = (video: HTMLVideoElement | null, isPlaying: boolean) => {
  const [bars, setBars] = useState(waveformBars);

  useEffect(() => {
    if (!video || typeof window === 'undefined')
      return;

    const audioWindow = window as AudioWindow;
    const mediaElement = video as CapturableVideoElement;
    const AudioContextCtor = audioWindow.AudioContext || audioWindow.webkitAudioContext;
    const captureStream = mediaElement.captureStream?.bind(mediaElement) ?? mediaElement.mozCaptureStream?.bind(mediaElement);
    if (!AudioContextCtor || !captureStream)
      return;

    let isCancelled = false;
    let animationFrame = 0;
    let audioContext: AudioContext | null = null;
    let source: MediaStreamAudioSourceNode | null = null;
    let analyser: AnalyserNode | null = null;
    let frequencyData: Uint8Array<ArrayBuffer> | null = null;
    let lastBars = waveformBars;

    const nextBars = () => {
      if (!analyser || !frequencyData)
        return lastBars;

      const data = frequencyData;
      analyser.getByteFrequencyData(data);

      const bucketSize = Math.max(1, Math.floor(data.length / waveformBars.length));
      lastBars = waveformBars.map((_height, index) => {
        const start = index * bucketSize;
        const end = Math.min(data.length, start + bucketSize);
        let sum = 0;

        for (let i = start; i < end; i++)
          sum += data[i];

        const average = sum / Math.max(1, end - start);
        return Math.max(6, Math.round((average / 255) * waveformHeight));
      });

      return lastBars;
    }

    const tick = () => {
      if (isCancelled)
        return;

      setBars(isPlaying ? nextBars() : lastBars.map(height => Math.max(6, Math.round(height * 0.88))));
      animationFrame = window.requestAnimationFrame(tick);
    }

    try {
      audioContext = new AudioContextCtor();
      source = audioContext.createMediaStreamSource(captureStream());
      analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.82;
      frequencyData = new Uint8Array(analyser.frequencyBinCount) as Uint8Array<ArrayBuffer>;
      source.connect(analyser);

      if (audioContext.state === 'suspended')
        void audioContext.resume();

      animationFrame = window.requestAnimationFrame(tick);
    } catch (_err) {
      setBars(waveformBars);
    }

    return () => {
      isCancelled = true;
      if (animationFrame)
        window.cancelAnimationFrame(animationFrame);
      source?.disconnect();
      analyser?.disconnect();
      void audioContext?.close();
    }
  }, [video, isPlaying]);

  return bars;
};

export const AudioPlayerMode = observer(({ model, mediaSlot, showInlineVideo }: Props) => {
  const posterUrl = model.job?.posterURL;
  const activeTopic = model.momentView.activeTopic;
  const topicName = activeTopic?.name ?? model.job?.title ?? 'No active topic';
  const bars = useAudioWaveformBars(model.adapter.videoElement, model.isPlaying);
  const allowShare = model.allowShare ?? true;
  const showShareButtons = model.job?.isPublic && allowShare;
  const posterStyle: CSSProperties = {
    backgroundImage: posterUrl ? `url('${posterUrl}')` : undefined
  };

  return (
    <div id="user-player-audio-mode">
      <div className="audio-player-card">
        <div className="audio-player-card-header">
          {showShareButtons && (
            <div className="audio-player-share-bar">
              <div className="control-group">
                <ToggleButton
                  model={model}
                  icon={<ShareSVGIcon />}
                  tooltip={'Share Video'}
                  onClick={() => model.shareVideo()} />
              </div>

              <div className="control-group">
                <ToggleButton
                  model={model}
                  icon={<DownloadSVGIcon />}
                  tooltip={'Share Assets'}
                  onClick={() => model.shareMeetingAssets()} />
              </div>
            </div>
          )}

          <div className="audio-player-toggle-bar">
            <PlayerToggleBar model={model} />
          </div>
        </div>

        <div
          className={classNames('audio-player-thumbnail', {
            'has-inline-video': showInlineVideo
          })}
          style={showInlineVideo ? undefined : posterStyle}>
          {showInlineVideo ?
            mediaSlot :
            (!posterUrl && <PodcastPlaceholderSvg className="podcast-placeholder-size" />)}
        </div>

        {!showInlineVideo && mediaSlot}

        <div className="audio-player-body">
          <div className="audio-player-main">
            <div className="audio-waveform-row">
              <div className="audio-waveform" role="img" aria-label="Audio waveform">
                <svg
                  className="audio-waveform-graphic"
                  viewBox={`0 0 ${waveformWidth} ${waveformHeight}`}
                  role="presentation"
                  focusable="false">
                  {bars.map((height, index) => (
                    <rect
                      key={index}
                      className="audio-waveform-bar"
                      x={index * (waveformBarWidth + waveformBarGap)}
                      y={(waveformHeight - height) / 2}
                      width={waveformBarWidth}
                      height={height}
                      rx={waveformBarWidth / 2}
                    />
                  ))}
                </svg>
              </div>
            </div>

            <PlayerMarkerBar model={model.commentsBar} />
            <PlayerPlaybar
              model={model}
              policy={model.getComponentPolicy(PlayerComponentName.ProgressBar)}
            />
          </div>

          <div className="audio-topic-card">
            <div className="audio-topic-icon">
              <PlayerBookmarkSVGIcon pressed={model.momentHasBookmark(activeTopic)} />
            </div>
            <div className="audio-topic-copy">
              <div className="audio-topic-label">Current topic</div>
              <div className="audio-topic-name">{topicName}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="audio-player-controls">
        <PlayerControlsBar model={model} ignoreChromeVisibilityForReactions />
      </div>
    </div>
  );
});
